import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import isString from 'lodash.isstring';
import {GroupSuggest} from 'src/components/group-suggest';
import {DateRangeInput} from 'src/components/input';
import {UserLink} from 'src/components/links';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {MessageContext} from 'src/contexts/message-context';
import {ValidatedControl} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {
    selectAccountLicense,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {analyzeUsageCriteria} from 'src/openapi/admin/apis/UsageReportsCriteriaApi';
import {downloadReportById, getCriteriaByReportId} from 'src/openapi/admin/apis/UsageReportsV2Api';
import {runUserSearch} from 'src/openapi/admin/apis/UserSearchApi';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {UserSortField as UserSortFields} from 'src/openapi/admin/models/UserSortField';
import {UserStateCriteria as UserStates} from 'src/openapi/admin/models/UserStateCriteria';
import {getUserLabel} from 'src/utils/getUserLabel';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import Button from '@pb/kit-component-button';
import {
    Drawer,
    DrawerFooter,
    DrawerHeader,
    DrawerHole,
    DrawerMain,
} from '@pb/kit-component-drawer';
import DropDownTree from '@pb/kit-component-dropdown-tree';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import FormSection from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import {LocaleContext} from '@pb/kit-context-locale';
import {pluralize} from '@pb/kit-formatters';

import {UserStateSuggest} from '../../components/user-state-suggest';
import {UsersSuggest} from '../../components/users-suggest';
import {isValidDateRange, validateEndDate} from '../../components/utils/dateValidationUtils';

import {
    DATE_RANGE_OPTIONS,
    REPORT_OPTIONS_TREE,
    SELECT_OPTIONS,
} from './constants';

import type {GeneratedReport} from './typedef';
import type {GeneratedReportCriteria} from 'src/openapi/admin/models/GeneratedReportCriteria';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';
import type {UserStateCriteria} from 'src/openapi/admin/models/UserStateCriteria';
import type {UserUsageCriteria} from 'src/openapi/admin/models/UserUsageCriteria';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';
import type {ServerResponse} from 'src/typedef';


type Props = {
    report?: GeneratedReport,
    accountId: number,
    open: boolean,
    onSyncRun: (criteria: UserUsageCriteria) => void,
    onAsyncRun: (criteria: UserUsageCriteria) => void,
    onClose: () => void,
};

export const ExcelPluginUsageReportDrawer = ({report, accountId, open, onClose, onSyncRun, onAsyncRun}: Props) => {
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();
    const [reportResponse, setReportResponse] = useState<ServerResponse<GeneratedReportCriteria> | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const {setSuccessMessage} = useContext(MessageContext);
    const {locale} = useContext(LocaleContext);
    const [downloadingFiles, setDownloadingFiles] = useState<string[]>([]);

    const license = useSelector(selectAccountLicense);
    const {getCalendarMonthPeriod} = useDateTime();

    const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS.CUSTOM.value);
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [userStates, selectUserStates] = useState<UserStateCriteria[]>([UserStates.LICENSED]);

    const [selectMode, setSelectMode] = useState(SELECT_OPTIONS.GROUPS.value);
    const [selectedUsers, setSelectedUsers] = useState<(UserSearchResult | string)[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
    const [reportOptions, setReportOptions] = useState<string[]>([
        'companySummaryReport',
        'userSummaryReport',
        'userActivityTrend',
    ]);
    const tabsRef = useRef<HTMLElement | null>(null);

    const localeDateFormat = mapDateFormat(locale.dateFormat, 'NUMERIC');

    useEffect(() => {
        tabsRef.current = document.querySelector('.tabs');
    }, []);

    useEffect(() => {
        const loadReport = async (id: string) => {
            const response = await apiDispatch(getCriteriaByReportId({reportId: id}));
            if (response.payload.success) {
                setReportResponse({data: response.payload.data});

                setStartDate(response.payload.data.startDate);
                setEndDate(response.payload.data.endDate);
                selectUserStates(response.payload.data.userStates || []);
                if (response.payload.data.userIds?.length) {
                    setSelectMode(SELECT_OPTIONS.USERS.value);
                    const userSearchResponse = await apiDispatch(runUserSearch({
                        userSearchRequest: {
                            searchCriteria: {
                                userIds: response.payload.data.userIds,
                            },
                            userSortCriteria: {
                                columnId: UserSortFields.EMAIL,
                                direction: SortDirections.ASC,
                            },
                            pagination: {
                                currentPage: 1,
                                pageSize: 250,
                            },
                        },
                    }));

                    if (userSearchResponse.payload.success) {
                        setSelectedUsers(userSearchResponse.payload.data.result);
                    }
                } else if (response.payload.data.groupId) {
                    setSelectMode(SELECT_OPTIONS.GROUPS.value);
                    setSelectedGroup(response.payload.data.groupId);
                }

                setReportOptions([
                    ...(response.payload.data.companySummaryReport ? ['companySummaryReport'] : []),
                    ...(response.payload.data.userSummaryReport ? ['userSummaryReport'] : []),
                    ...(response.payload.data.userActivityTrend ? ['userActivityTrend'] : []),
                    ...(response.payload.data.projectCodeSummaryReport ? ['projectCodeSummaryReport'] : []),
                ]);
            } else {
                setReportResponse({error: response.payload.data});
            }
        };

        if (report) {
            setReportResponse(null);
            setErrorMessage('');
            setDownloadingFiles([]);
            setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
            setStartDate(undefined);
            setEndDate(undefined);
            selectUserStates([UserStates.LICENSED]);
            setSelectMode(SELECT_OPTIONS.GROUPS.value);
            setSelectedUsers([]);
            setSelectedGroup(null);
            setReportOptions([
                'companySummaryReport',
                'userSummaryReport',
                'userActivityTrend',
            ]);

            loadReport(report.reportId);
        }
    }, [report, apiDispatch]);

    const changeMode = useCallback((mode: string) => {
        if (mode === DATE_RANGE_OPTIONS.CURRENT_LICENSE.value && license) {
            setStartDate(license.startDate);
            setEndDate(license.endDate);
        } else if (mode === DATE_RANGE_OPTIONS.LAST_MONTH.value) {
            const period = getCalendarMonthPeriod();
            setStartDate(period.startDate);
            setEndDate(period.endDate);
        }

        setDateRange(mode);
    }, [license, getCalendarMonthPeriod]);

    const changeStartDate = useCallback((date: string) => {
        if (date !== startDate) {
            setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
        }

        setStartDate(date);
    }, [startDate]);

    const changeEndDate = useCallback((date: string) => {
        if (date !== endDate) {
            setDateRange(DATE_RANGE_OPTIONS.CUSTOM.value);
        }

        setEndDate(date);
    }, [endDate]);

    const validateEndDateWrapper = useCallback(() => {
        return validateEndDate(endDate, startDate, 'USAGE_REPORT_END_DATE');
    }, [endDate, startDate]);


    const changeSelectionMode = useCallback((mode: string) => {
        setSelectMode(mode);
        if (mode === SELECT_OPTIONS.GROUPS.value) {
            setSelectedUsers([]);
            selectUserStates([UserStates.LICENSED]);
        } else {
            setSelectedGroup(null);
        }
    }, []);

    const changeSelectedUsers = useCallback((users: (UserSearchResult | string)[]) => {
        setSelectedUsers(users);

        if (users.length) {
            selectUserStates([]);
        } else {
            selectUserStates([UserStates.LICENSED]);
        }
    }, []);

    const isRunDisabled = useMemo(() => {
        if (!reportOptions.length) {
            return true;
        }

        if (selectedUsers.length) {
            const validUsers = selectedUsers.filter(value => !isString(value));

            return !validUsers.length;
        }

        return !(userStates.length && isValidDateRange(endDate, startDate));
    }, [reportOptions, selectedUsers, userStates, startDate, endDate]);

    const analyzeCriteria = useCallback(async () => {
        if (!startDate || !endDate) {
            return;
        }

        const groupId = selectMode === SELECT_OPTIONS.GROUPS.value
            ? selectedGroup || undefined
            : undefined;
        const userIds = selectMode === SELECT_OPTIONS.USERS.value && selectedUsers.length
            ? selectedUsers
                    .filter(value => !isString(value))
                    .map(value => (value as UserSearchResult).id)
            : undefined;

        const userUsageCriteria: UserUsageCriteria = {
            accountId,
            startDate,
            endDate,
            userStates,
            groupId,
            userIds,
            ...Object.fromEntries(reportOptions.map(option => [option, true])),
        };

        const response = await apiDispatch(analyzeUsageCriteria({userUsageCriteria}));
        if (response.payload.success) {
            const isBigReport = response.payload.data.bigReport;
            if (isBigReport) {
                onAsyncRun(userUsageCriteria);
            } else {
                onSyncRun(userUsageCriteria);
            }
            onClose();
        } else {
            setErrorMessage(response.payload.data.message || 'Failed to analyze criteria. Please, try again later.');
        }
    }, [
        accountId,
        apiDispatch,
        userStates,
        onSyncRun,
        onAsyncRun,
        onClose,

        selectMode,
        selectedGroup,
        selectedUsers,
        reportOptions,
        startDate,
        endDate,
    ]);

    const downloadFile = useCallback(async (fileId: string) => {
        if (fileId && !downloadingFiles.includes(fileId)) {
            setDownloadingFiles([...new Set([...downloadingFiles, fileId])]);
            const response = await apiDispatch(downloadReportById({reportId: fileId}));
            setDownloadingFiles(downloadingFiles.filter(id => id !== fileId));

            if (response.payload.error) {
                setErrorMessage(response.payload.data.message || 'Failed to download report. Please, try again later.');
            } else {
                setSuccessMessage('The file has been downloaded successfully');
            }
        }
    }, [apiDispatch, setErrorMessage, setSuccessMessage, downloadingFiles]);

    const handleSetOpen = useCallback((shown: boolean) => {
        if (!shown) {
            onClose();
        }
    }, [onClose]);

    const drawerTitle = `Excel Plugin Usage Report on ${FormattedDate.format({
        value: report?.generatedAt,
        format: `${localeDateFormat} HH:mm [at] A`,
    })}`;

    return (
        <Drawer
            open={open}
            setOpen={handleSetOpen}
            title={drawerTitle}
            placement="right"
            stickToElement={tabsRef.current || undefined}
            style={{width: 'fit-content'}}>
            <DrawerHeader>
                {drawerTitle}
            </DrawerHeader>
            <DrawerMain>
                <DrawerHole>
                    {errorMessage ? (
                        <Message color="error" onClose={() => setErrorMessage('')}>{errorMessage}</Message>
                    ) : null}
                    <Skeleton shown={!report || !reportResponse} view={<Loading fast={true}/>}>
                        <ServerResponseHandler serverError={reportResponse?.error || null} smallView={true}>
                            <Group direction="v">
                                <FormSection headline="Configuration">
                                    <Group direction="v" justify="space-between" alignItems="start">
                                        <Group direction="v" alignItems="start">
                                            <Group direction="v" gap="xs">
                                                <Label><RequiredMark label="Date Range"/></Label>
                                                <RadioButtons
                                                    name="excel-plugin-usage-report-drawer-date-range-select"
                                                    value={dateRange}
                                                    onChange={changeMode}>
                                                    <Group gap="s">
                                                        <RadioButton {...DATE_RANGE_OPTIONS.CUSTOM}/>
                                                        <RadioButton {...DATE_RANGE_OPTIONS.LAST_MONTH}/>
                                                        <RadioButton {...DATE_RANGE_OPTIONS.CURRENT_LICENSE}/>
                                                    </Group>
                                                </RadioButtons>
                                                <DateRangeInput
                                                    id="usage-report-start-date-picker"
                                                    label={<>Starts</>}
                                                    width={300}
                                                    clearable={true}
                                                    startDate={startDate}
                                                    endDate={endDate}
                                                    selectRangeType="start"
                                                    onChange={changeStartDate}
                                                />
                                                <ValidatedControl
                                                    name="USAGE_REPORT_END_DATE"
                                                    validate={validateEndDateWrapper}>
                                                    {(invalid: boolean) => (
                                                        <DateRangeInput
                                                            id="usage-report-end-date-picker"
                                                            label={<>Ends&nbsp;&nbsp;</>}
                                                            width={300}
                                                            clearable={true}
                                                            invalid={invalid}
                                                            startDate={startDate}
                                                            endDate={endDate}
                                                            selectRangeType="end"
                                                            onChange={changeEndDate}
                                                        />
                                                    )}
                                                </ValidatedControl>
                                            </Group>
                                            <Group gap="l">
                                                <div style={{width: 300}}>
                                                    <Label><RequiredMark label="Include"/></Label>
                                                    <DropDownTree
                                                        data={REPORT_OPTIONS_TREE}
                                                        checkedIdsList={reportOptions}
                                                        inputProps={{placeholder: 'Select report options'}}
                                                        onTreeChange={params => setReportOptions(params.checkedIdsList)}
                                                    />
                                                </div>
                                                <div style={{width: 300}}>
                                                    <Label>User States</Label>
                                                    <UserStateSuggest
                                                        disabled={selectedUsers.length > 0}
                                                        userStates={userStates}
                                                        onSelect={selectUserStates}
                                                    />
                                                </div>
                                            </Group>
                                            <Group direction="v" gap="xs">
                                                <Label>Select</Label>
                                                <RadioButtons
                                                    name="excel-plugin-usage-report-drawer-select"
                                                    value={selectMode}
                                                    onChange={changeSelectionMode}>
                                                    <Group gap="l">
                                                        <RadioButton {...SELECT_OPTIONS.USERS}/>
                                                        <RadioButton {...SELECT_OPTIONS.GROUPS}/>
                                                    </Group>
                                                </RadioButtons>
                                                {selectMode === SELECT_OPTIONS.USERS.value && (
                                                    <div style={{width: 624}}>
                                                        <UsersSuggest
                                                            id="excel-plugin-usage-report-users-suggest"
                                                            accountId={accountId}
                                                            selectedUsers={selectedUsers}
                                                            setSelectedUsers={changeSelectedUsers}
                                                            placeholder="All Users"
                                                        />
                                                    </div>
                                                )}
                                                {selectMode === SELECT_OPTIONS.GROUPS.value && (
                                                    <GroupSuggest
                                                        id="excel-plugin-usage-report-group-suggest"
                                                        width={300}
                                                        accountId={accountId}
                                                        selectedGroupId={selectedGroup}
                                                        placeholder="All Groups"
                                                        onSelect={group => setSelectedGroup(group?.id || null)}
                                                    />
                                                )}
                                            </Group>
                                        </Group>
                                    </Group>
                                </FormSection>
                                <FormSection headline="Generated Report">
                                    <Group direction="v" alignItems="start">
                                        {reportResponse?.data ? (
                                            <div style={{width: 300}}>
                                                <Label>Requester</Label>
                                                <UserLink
                                                    userId={reportResponse.data.adminInfo.userId}
                                                    caption={getUserLabel(reportResponse.data.adminInfo)}
                                                    place="Excel Plugin Usage Report Drawer"
                                                />
                                            </div>
                                        ) : null}
                                        {report?.files ? (
                                            <div style={{width: 600}}>
                                                <Label>{pluralize('File[s]', report.files.length)}</Label>
                                                <Group direction="v">
                                                    {report.files.map(({name, id}) => (
                                                        <Group alignItems="center" gap="xs" key={id}>
                                                            {downloadingFiles.includes(id)
                                                                ? <Loading type="ICON" fast={true}/>
                                                                : <Icon type={Icons.EXCEL}/>}
                                                            <EllipsisTextHint title={name}>
                                                                <a onClick={() => downloadFile(id)}>{name}</a>
                                                            </EllipsisTextHint>
                                                        </Group>
                                                    ))}
                                                </Group>
                                            </div>
                                        ) : null}
                                    </Group>
                                </FormSection>
                            </Group>
                        </ServerResponseHandler>
                    </Skeleton>
                </DrawerHole>
            </DrawerMain>
            <DrawerFooter>
                <Group>
                    <Button variant="Type.PRIMARY" onClick={analyzeCriteria} disabled={isRunDisabled}>
                        Run Again
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Close
                    </Button>
                </Group>
            </DrawerFooter>
        </Drawer>
    );
};
